import { cloneElement, isValidElement, useCallback, useMemo, useRef, useEffect } from "react";
import type { AriaAttributes, ReactElement } from "react";

import { ConfigProvider, Table, Row, TableProps, Col } from "antd";
import { SpinProps } from "antd/lib/spin";
import { clsx } from "clsx";
import map from "lodash/map";
import noop from "lodash/noop";

import { immutableSplice } from "../../../../services/DataService/DataService";
import { RVLoading } from "../../../atoms/RVLoading/RVLoading";
import { handleTableChange } from "../utils/GridQueryUtils/GridQueryUtils";
import { DragSortingTable, DraggableBodyRow, DraggableRowProps } from "./DragSortingTable/DragSortingTable";

import type { GridProps, QueryParams } from "../RVGridTypes";
import type { TablePaginationConfig } from "antd";

import styles from "../RVGrid.module.scss";

/**
 * Extension of `GridProps` used when the table state is managed outside of the React Router query
 * string. `setUiQueryParams` and `uiQueryParams` mirror the router hooked version but point to
 * whatever state container the parent supplies (local component state, Zustand store, etc.).
 *
 * The QueryParams type uses loose value unions (`string | boolean | any[] | null`) because each
 * column/filter can map to wildly different primitives, and locking it down here would force
 * needless conversions in consumers.
 */
export type RVNonRouterGridProps = GridProps & {
    setUiQueryParams: (params: Partial<QueryParams<string | boolean | any[] | null>>) => void;
    uiQueryParams: Partial<QueryParams>;
    customEmpty?: React.ReactNode;
};

/**
 * Low-level grid for contexts **without** React Router (modals, slideouts, alerts, embedded flows).
 *
 * Mirrors {@link RVGrid} behavior but requires the parent to supply `uiQueryParams` and
 * `setUiQueryParams` (typically from `useQueryParamsV6` or local state) instead of syncing to the
 * URL. ModalProvider sits outside the app router tree, so modals cannot use {@link RVGrid} directly.
 *
 * @remarks Do **not** use on routed list pages — use {@link RVGrid} instead. Infinite scroll and
 * cursor pagination are supported by both components; router context determines the choice, not
 * pagination style.
 *
 * @see {@link RVGrid} — default for any page under `src/Pages/` registered in `Routes.tsx`.
 */
export function RVNonRouterGrid({
    additionalFilters = [],
    className = "",
    columns = [],
    columnsData = [],
    rowKey,
    header = undefined,
    flexibleHeader = undefined,
    footer = undefined,
    loading = false,
    totalRecords = undefined,
    pageWidth = "standard",
    rowClassName = noop,
    rowSelection = undefined,
    tableSize = "default",
    onRow = noop,
    filterOperators = {},
    draggable = false,
    onSortData,
    pagination = true,
    scroll,
    useQueryParamsUrl = true,
    infiniteScroll = false,
    getMoreRows,
    headerSpacing = "medium",
    gutter = [16, 28],
    "data-testid": dataTestId,
    pageSizeOptions,
    setUiQueryParams,
    uiQueryParams,
    customEmpty,
}: RVNonRouterGridProps): JSX.Element {
    const hideNoDataOnLoading = () => <></>;
    const tableLoading: SpinProps = {
        indicator: <RVLoading text={null} />,
        spinning: loading,
        style: { top: "75px" },
    };

    const root = useRef<HTMLDivElement>(null);

    const sizedColumns = useMemo(
        () =>
            map(columns, (column) => ({
                ...column,
                // Wrap bare string/number header titles in a <span> so browser translation tools
                // (Google Translate, translate extensions) swap an element instead of a raw text
                // node React is tracking. Non-sortable AntD headers otherwise render the title as a
                // bare <th> text node, which triggers the "Failed to execute 'removeChild'" crash.
                // See ../../../../../../.github/instructions/frontend.instructions.md → Browser
                // Translation / Extension DOM Crashes.
                title:
                    typeof column.title === "string" || typeof column.title === "number" ? (
                        <span>{column.title}</span>
                    ) : (
                        column.title
                    ),
                className: clsx(column.className, styles.column, styles[column.size]),
            })),
        [columns]
    );

    /**
     * Computes the scrollable height for the Ant table body. The math intentionally happens in JS
     * because the containing layout varies (modal, drawer, page) and we need to account for header
     * offsets, pagination, and inner padding.
     */
    function getTableScroll() {
        // Hate calculating heights in JS, but don't want to break stuff
        // this space accounts for the pagination, margin and padding below the table
        const extraHeight = 72;
        const tHeader = document.getElementsByClassName("ant-table-thead")[0] as HTMLElement;
        // The distance from the table content to the top
        let tHeaderBottom = 0;
        let tHeaderTop = 0;
        let tHeaderHeight = 0;
        if (tHeader) {
            const rect = tHeader.getBoundingClientRect();
            tHeaderBottom = rect.bottom;
            tHeaderTop = rect.top;
            tHeaderHeight = rect.height;
        }

        if (infiniteScroll) {
            const mainContent = document.getElementsByTagName("main")[0] as HTMLElement | undefined;
            const mainScrollTop = mainContent?.scrollTop ?? 0;
            return `calc(100vh - ${tHeaderTop + mainScrollTop + extraHeight + tHeaderHeight}px)`;
        }

        // Form height - The height of the top of the table content - The height of the bottom of the table content
        return `calc(100vh - ${tHeaderBottom + extraHeight}px)`;
    }

    useEffect(() => {
        if (root.current && infiniteScroll && columnsData.length) {
            const tableBody = root.current.getElementsByClassName("ant-table-body")[0];

            function handleScroll() {
                const { scrollTop, scrollHeight, clientHeight } = tableBody;

                if (scrollTop + clientHeight === scrollHeight) {
                    getMoreRows?.();
                }
            }

            tableBody.addEventListener("scroll", handleScroll, { passive: true });

            return () => tableBody.removeEventListener("scroll", handleScroll);
        }
    }, [infiniteScroll, getMoreRows]);

    // Keeps draggable reordering side-effect free by using immutable array operations.
    const moveRow = useCallback(
        (sourceIndex: number, targetIndex: number) => {
            const sourceRow = columnsData[sourceIndex];
            const reorderedArray = immutableSplice(
                immutableSplice(columnsData, sourceIndex, 1), // Removes the dragged from its position, and then insert it on the desired index
                targetIndex,
                0,
                sourceRow
            );

            onSortData?.(reorderedArray);
        },
        [columnsData, onSortData]
    );

    const dragSortingTableComponents = {
        body: {
            row: DraggableBodyRow,
        },
    };

    const paginationConfig: TablePaginationConfig | false =
        pagination &&
        ({
            defaultPageSize: uiQueryParams.pageSize,
            size: "small",
            pageSize: uiQueryParams.pageSize || undefined,
            pageSizeOptions: pageSizeOptions ?? ["5", "10", "25", "50", "100"],
            total: totalRecords,
            current: uiQueryParams.page,
            showSizeChanger: true,
            itemRender: (page, type, originalElement) => {
                if ((type === "prev" || type === "next") && isValidElement(originalElement)) {
                    const ariaLabel = type === "prev" ? "Previous Page" : "Next Page";
                    const paginationButton = originalElement as ReactElement<AriaAttributes>;
                    return cloneElement(paginationButton, { "aria-label": ariaLabel });
                }
                return originalElement;
            },
        } as TablePaginationConfig);

    const tableProps: TableProps<any> = {
        rowClassName: rowClassName,
        rowSelection: rowSelection,
        rowKey: rowKey,
        className: styles.scrollableContent,
        columns: sizedColumns,
        dataSource: columnsData,
        pagination: paginationConfig,
        scroll: scroll
            ? scroll
            : columnsData.length
              ? { x: true, y: getTableScroll(), scrollToFirstRowOnChange: false }
              : // if no data, do not squeeze the columns.
                { x: true },
        tableLayout: "fixed",
        size: tableSize as any,
        loading: tableLoading,
        onChange: useQueryParamsUrl
            ? handleTableChange({
                  action: setUiQueryParams,
                  uiQueryParams,
                  columns,
                  additionalFilters,
                  filterOperators,
              })
            : undefined,
    };

    return (
        <>
            <div
                ref={root}
                className={clsx(styles.gridContainer, className, {
                    [styles.standard]: pageWidth == "standard",
                    [styles.widePage]: pageWidth == "wide",
                })}
                data-testid={dataTestId}
            >
                {header && (
                    <Row
                        align="middle"
                        justify="space-between"
                        gutter={gutter}
                        className={clsx({
                            [styles.headerSpacingSmall]: headerSpacing == "small",
                            [styles.headerSpacingMedium]: headerSpacing == "medium",
                        })}
                    >
                        {header}
                    </Row>
                )}
                {flexibleHeader}

                <ConfigProvider renderEmpty={() => (loading ? hideNoDataOnLoading() : customEmpty)}>
                    {draggable ? (
                        <div style={{ position: "relative", zIndex: 0 }}>
                            <DragSortingTable
                                {...tableProps}
                                components={columnsData?.length ? dragSortingTableComponents : undefined}
                                onRow={(_: any, index?: number) =>
                                    ({
                                        index,
                                        moveRow,
                                    }) as DraggableRowProps
                                }
                            />
                        </div>
                    ) : (
                        <Table {...tableProps} showSorterTooltip={false} onRow={onRow} />
                    )}

                    {footer && (
                        <Col
                            sm={12}
                            className={clsx(styles.footer, {
                                [styles.alignFooterToPagination]:
                                    columnsData.length > (uiQueryParams?.pageSize ?? 0) && !loading,
                            })}
                        >
                            {footer}
                        </Col>
                    )}
                </ConfigProvider>
            </div>
        </>
    );
}
